/**
 * Nudge Rule Library — browsable admin gallery of published custom nudge rules
 * from every workspace. Admins can install any rule into their own workspace
 * with one click (idempotent — reinstalling returns the existing local copy).
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sparkles, Search, Users, Download, Check, Globe, User as UserIcon,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function NudgeRuleLibrary() {
  const { isAdmin } = useAuth();
  const [rules, setRules] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);   // id currently being installed

  const load = async () => {
    try {
      const r = await api.get("/bot-access/nudge-rules/library");
      setRules(r.data.rules || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load library");
    }
  };
  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rules;
    return rules.filter((r) =>
      r.name.toLowerCase().includes(s) ||
      (r.keyword || "").toLowerCase().includes(s) ||
      (r.body || "").toLowerCase().includes(s),
    );
  }, [rules, q]);

  const install = async (r) => {
    setBusy(r.id);
    try {
      await api.post(`/bot-access/nudge-rules/library/${r.id}/install`);
      toast.success(`Installed "${r.name}" — active in this workspace`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to install");
    } finally { setBusy(null); }
  };

  if (!isAdmin) {
    return <div className="p-8 max-w-2xl mx-auto text-sm text-muted-foreground" data-testid="nudge-library-forbidden">Admin-only.</div>;
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="nudge-rule-library-page">
      <header>
        <p className="text-[10px] uppercase tracking-[0.28em] text-sky-500">Nudge library</p>
        <h1 className="font-display text-3xl mt-1">Community nudge rules</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Every rule admins have published across the network. One-click install any pattern into this workspace — installs stay local and can be edited or disabled anytime.
        </p>
      </header>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          data-testid="nudge-library-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name, keyword, or message…"
          className="pl-9"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="card-flat p-6 text-center text-sm text-muted-foreground">
          {rules.length === 0
            ? "No published rules yet. Once admins publish rules from Settings → Custom nudge rules they'll appear here."
            : "No rules match this search."}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((r) => (
            <article key={r.id}
                     data-testid={`nudge-library-card-${r.id}`}
                     className={`rounded-lg border p-4 flex flex-col gap-2 ${r.tone === "warn"
                       ? "border-amber-500/40 bg-amber-500/5"
                       : "border-border/60 bg-secondary/10 hover:bg-secondary/20"}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{r.name}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${r.tone === "warn" ? "bg-amber-500/15 text-amber-500" : "bg-sky-500/15 text-sky-500"}`}>
                      {r.tone}
                    </span>
                    <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-500">
                      <Globe className="w-2.5 h-2.5 inline mr-0.5" /> published
                    </span>
                    {r.install_count > 0 && (
                      <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-fuchsia-500/15 text-fuchsia-500"
                            title="Times installed across the network">
                        <Users className="w-2.5 h-2.5 inline mr-0.5" /> {r.install_count}
                      </span>
                    )}
                  </div>
                </div>
                <span className="text-xs text-muted-foreground font-mono shrink-0">≥{r.threshold}/{r.window_days}d</span>
              </div>
              <p className="text-xs text-muted-foreground line-clamp-2">
                Fires on <span className="font-mono">"{r.keyword}"</span> — {r.body}
              </p>
              <p className="text-[10px] text-muted-foreground">
                <UserIcon className="w-2.5 h-2.5 inline mr-1" />
                by {r.owner?.name || r.owner?.email || "unknown"}
              </p>
              {r.installed_by_me ? (
                <Button size="sm" variant="outline" disabled
                        className="mt-auto"
                        data-testid={`nudge-library-installed-${r.id}`}>
                  <Check className="w-3.5 h-3.5 mr-1.5" /> Installed
                </Button>
              ) : (
                <Button size="sm" className="mt-auto"
                        onClick={() => install(r)}
                        disabled={busy === r.id || r.is_mine_author}
                        title={r.is_mine_author ? "You authored this rule" : ""}
                        data-testid={`nudge-library-install-${r.id}`}>
                  <Download className="w-3.5 h-3.5 mr-1.5" />
                  {r.is_mine_author ? "Your rule" : "Install"}
                </Button>
              )}
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
